/**
 * Team: in-call teaming in the interpreter's Tools (docs/interpreter-teaming.md,
 * vrs-server/ws/interpreter-teams.js). Shared by the web panel
 * (components/web/TeamSection.tsx) and the Terp sheet's Team section
 * (vrs-layout/components/InterpreterCallTools.native.tsx).
 *
 * Teaming applies to every queue call in the room. The call's interpreter
 * asks (anyone available, or one colleague from the transfer picker's list),
 * waits, and can end the team; a teammate who joined the call
 * (`team` on the match or the stored call, interpreter-queue/teamOffers.ts)
 * sees only Leave team. `pending` is true while a request waits.
 */
import { useCallback, useEffect, useState } from 'react';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { callTeamLine, initialCallTeam, reduceCallTeam } from '../interpreter-queue/teamOffers';
/**
 * What the Tools make of a call's team state.
 *
 * @param {string} [callId] - The queue call this room belongs to.
 * @param {CallTeamState} state - Its team, as the tools follow it.
 * @returns {Object}
 */
export function teamToolState(callId, state) {
    return {
        applies: Boolean(callId),
        line: callTeamLine(state),
        pending: state.phase === 'requesting' || state.phase === 'pending',
        teammate: state.phase === 'teammate'
    };
}
/**
 * Teaming for the call in this room.
 *
 * @param {string} [callId] - The queue call this room belongs to.
 * @param {IJoinedTeam} [joined] - The team this interpreter joined as the teammate.
 * @returns {ITeamTool}
 */
export function useTeamTool(callId, joined) {
    const joinedId = joined?.teamId;
    const joinedPrimary = joined?.primaryInterpreterName;
    const [state, setState] = useState(() => initialCallTeam(joined));
    useEffect(() => {
        setState(initialCallTeam(joinedId ? { primaryInterpreterName: joinedPrimary, teamId: joinedId } : undefined));
        if (!callId) {
            return undefined;
        }
        const forThisCall = (data) => Boolean(data) && typeof data === 'object' && data.callId === callId;
        const follow = (type) => (data) => {
            if (forThisCall(data)) {
                setState(previous => reduceCallTeam(previous, { data, type }));
            }
        };
        const onPending = follow('pending');
        const onAccepted = follow('accepted');
        const onRequestEnded = follow('requestEnded');
        const onEnded = follow('ended');
        const onRefused = (data) => {
            if (forThisCall(data) && data.operation === 'team_request') {
                setState(previous => reduceCallTeam(previous, { code: data.code, type: 'refused' }));
            }
        };
        queueService.on('teamRequestPending', onPending);
        queueService.on('teamAccepted', onAccepted);
        queueService.on('teamRequestEnded', onRequestEnded);
        queueService.on('teamEnded', onEnded);
        queueService.on('error', onRefused);
        return () => {
            queueService.off('teamRequestPending', onPending);
            queueService.off('teamAccepted', onAccepted);
            queueService.off('teamRequestEnded', onRequestEnded);
            queueService.off('teamEnded', onEnded);
            queueService.off('error', onRefused);
        };
    }, [callId, joinedId, joinedPrimary]);
    const request = useCallback((toInterpreterId) => {
        if (!callId) {
            return;
        }
        setState(previous => reduceCallTeam(previous, { toInterpreterId, type: 'request' }));
        queueService.requestTeam(callId, toInterpreterId);
    }, [callId]);
    const cancel = useCallback(() => {
        if (state.phase === 'pending') {
            queueService.cancelTeamRequest(state.teamId);
        }
    }, [state]);
    const end = useCallback(() => {
        if (state.phase === 'teamed' || state.phase === 'teammate') {
            queueService.leaveTeam(state.teamId);
        }
    }, [state]);
    return { ...teamToolState(callId, state), cancel, end, request, state };
}
